export interface SyncMediaParams {
  isPlaying: boolean;
  playheadTime: number;
  layerStart: number;
  layerEnd: number;
  trimStart: number;
  trimEnd: number;
  loop: boolean;
  volume: number;
  /** Debug label — when set, drift/seek events for this element are logged. */
  debugLabel?: string;
}

/** Toggle in DevTools console: `window.__KINEMA_AUDIO_DEBUG = true`. */
declare global { interface Window { __KINEMA_AUDIO_DEBUG?: boolean } }

// When paused: seek precisely so the displayed frame matches the playhead.
// When playing: let the element advance on its own clock and only correct on
// large drift — a tight threshold here would re-seek every editor tick (since
// the snapped playhead lags the element's free-running currentTime by up to a
// frame), which stutters playback.
const SEEK_THRESHOLD_PAUSED = 0.02;
const SEEK_THRESHOLD_PLAYING = 0.2;

/**
 * Drives a media element (audio or video) from editor state.
 *
 * - Always seeks the element toward the right source-time so the displayed
 *   frame / audio position matches the playhead, even when paused.
 * - Plays only when `isPlaying` is true and the playhead is inside the layer
 *   window. Pauses otherwise.
 *
 * Source-time math: `localTime = playheadTime - layerStart`, then map into
 * `[trimStart, sourceDuration - trimEnd]`. With `loop`, wraps within that
 * range; without, clamps to the tail.
 */
export function syncMediaElement(el: HTMLMediaElement, params: SyncMediaParams): void {
  const { isPlaying, playheadTime, layerStart, layerEnd, trimStart, trimEnd, loop, volume, debugLabel } = params;
  const debug = debugLabel && typeof window !== 'undefined' && window.__KINEMA_AUDIO_DEBUG;

  const isInWindow = playheadTime >= layerStart && playheadTime <= layerEnd;
  const duration = el.duration;

  if (!isInWindow) {
    if (!el.paused) el.pause();
    return;
  }

  if (Number.isFinite(duration) && duration > 0) {
    const usableLen = Math.max(0, duration - trimStart - trimEnd);
    if (usableLen > 0) {
      const localTime = Math.max(0, playheadTime - layerStart);
      const sourceTime = loop
        ? trimStart + ((localTime % usableLen) + usableLen) % usableLen
        : trimStart + Math.min(localTime, usableLen);

      const threshold = isPlaying ? SEEK_THRESHOLD_PLAYING : SEEK_THRESHOLD_PAUSED;
      const drift = el.currentTime - sourceTime;
      if (debug && isPlaying) {
        console.log(`[sync ${debugLabel}] drift=${(drift * 1000).toFixed(1)}ms playhead=${playheadTime.toFixed(3)} ct=${el.currentTime.toFixed(3)} target=${sourceTime.toFixed(3)} ready=${el.readyState}`);
      }
      if (Math.abs(drift) > threshold) {
        if (debug) console.warn(`[sync ${debugLabel}] SEEK from ${el.currentTime.toFixed(3)} → ${sourceTime.toFixed(3)} (drift ${(drift * 1000).toFixed(1)}ms)`);
        el.currentTime = sourceTime;
      }
    } else {
      if (!el.paused) el.pause();
      return;
    }
  }

  const clampedVolume = Math.max(0, Math.min(1, volume));
  if (el.volume !== clampedVolume) {
    el.volume = clampedVolume;
  }

  if (isPlaying) {
    if (el.paused) {
      if (debug) console.log(`[sync ${debugLabel}] PLAY at ct=${el.currentTime.toFixed(3)}`);
      el.play().catch((err) => {
        if (debug) console.warn(`[sync ${debugLabel}] play() rejected:`, err);
      });
    }
  } else {
    if (!el.paused) {
      if (debug) console.log(`[sync ${debugLabel}] PAUSE at ct=${el.currentTime.toFixed(3)}`);
      el.pause();
    }
  }
}
